import { Close, EventOutlined, LockOutlined, PlayCircleOutline } from "@mui/icons-material"
import { Box, IconButton, InputBase, Skeleton, Tooltip, Typography, useMediaQuery, useTheme } from "@mui/material"
import React, { useState } from "react"
import { Media } from "../../Model/MediaModel"
import { PlaylistItems } from "../../Model/PlaylistModel"
import { resolveMediaUrl } from "../../Util/mediaUrl"
import config from "../../Util/config"

const MIN_SEC = config.FEATURE.MIN_SECONDS
const MAX_SEC = config.FEATURE.MAX_SECONDS

type Props = {
  item: PlaylistItems
  add: (n: number, item: PlaylistItems) => void
  sub: (n: number, item: PlaylistItems) => void
  removeItem: (item: PlaylistItems) => void
  isFixed: boolean
  onSchedule: (item: PlaylistItems) => void
}

const CardMidia = React.memo((props: Props) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))

  const [imgLoaded, setImgLoaded] = useState(false)
  const [imgError, setImgError] = useState(false)
  const [draft, setDraft] = useState<string | null>(null)

  const isVideo = props.item.type === "video"
  const isLocked = isVideo || props.isFixed
  const media = props.item.data as Media | undefined
  const order = String(props.item.order).padStart(2, '0')

  if (!media) return <Skeleton variant="rectangular" width="100%" height="100%" animation="wave" sx={{ borderRadius: 1.5 }} />

  function applyDraft() {
    if (draft === null) return
    const val = parseInt(draft, 10)
    if (!isNaN(val) && val > 0) {
      const clamped = Math.min(Math.max(val, MIN_SEC), MAX_SEC)
      const diff = clamped - props.item.seconds
      if (diff > 0) props.add(diff, props.item)
      else if (diff < 0) props.sub(-diff, props.item)
    }
    setDraft(null)
  }

  const durationDisplay = draft !== null ? draft : String(props.item.seconds)

  if (isMobile) {
    return (
      <Box sx={{ width: '100%' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', px: 1.5, pt: 1, pb: 0.5, gap: 1 }}>
          <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: 'text.disabled', letterSpacing: '0.05em' }}>
            {order}
          </Typography>
          <Typography variant="caption" color="text.secondary" fontWeight={500}>
            {isVideo ? 'Vídeo' : 'Imagem'}
          </Typography>
          <Box sx={{ flex: 1 }} />
          <Tooltip title={props.item.schedule ? 'Agendamento ativo' : 'Agendar'}>
            <IconButton size="small" onClick={() => props.onSchedule(props.item)}
              sx={{ width: 44, height: 44, color: props.item.schedule ? 'primary.main' : 'text.disabled' }}>
              <EventOutlined fontSize="small" />
            </IconButton>
          </Tooltip>
          <IconButton size="small" onClick={() => props.removeItem(props.item)}
            sx={{ width: 44, height: 44, '&:hover': { color: 'error.main' } }}>
            <Close fontSize="small" />
          </IconButton>
        </Box>

        <Box sx={{ mx: 1.5, borderRadius: 1.5, overflow: 'hidden', position: 'relative', height: 160, bgcolor: 'grey.100' }}>
          {!imgLoaded && !imgError && (
            <Skeleton variant="rectangular" width="100%" height="100%" animation="wave" />
          )}
          {!imgError && (
            <Box component="img"
              src={resolveMediaUrl(media.urlPreview)}
              alt={media.desc}
              onLoad={() => setImgLoaded(true)}
              onError={() => { setImgLoaded(true); setImgError(true) }}
              sx={{ width: '100%', height: '100%', objectFit: 'cover', display: imgLoaded ? 'block' : 'none' }}
            />
          )}
          {imgError && (
            <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Typography variant="caption" color="text.disabled">Sem preview</Typography>
            </Box>
          )}
          {isVideo && imgLoaded && !imgError && (
            <PlayCircleOutline sx={{
              position: 'absolute', top: '50%', left: '50%',
              transform: 'translate(-50%,-50%)',
              fontSize: 48, color: 'rgba(255,255,255,0.9)', pointerEvents: 'none',
            }} />
          )}
        </Box>

        <Box sx={{ px: 1.5, pt: 1, pb: 1.5 }}>
          <Typography variant="body2" fontWeight={500} noWrap sx={{ mb: 1 }}>
            {media.desc}
          </Typography>
          {isLocked ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <LockOutlined sx={{ fontSize: 13, color: 'text.disabled' }} />
              <Typography variant="body2" color="text.secondary">
                {isVideo ? `${props.item.seconds}s (vídeo)` : 'Tempo fixo'}
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>Duração:</Typography>
              <IconButton size="small" onClick={() => props.sub(1, props.item)} sx={{ width: 44, height: 44 }}>
                <Typography sx={{ fontSize: 20, lineHeight: 1, fontWeight: 300 }}>−</Typography>
              </IconButton>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.25 }}>
                <InputBase
                  value={durationDisplay}
                  onChange={e => setDraft(e.target.value.replace(/\D/g, ''))}
                  onFocus={e => { setDraft(String(props.item.seconds)); e.target.select() }}
                  onBlur={applyDraft}
                  onKeyDown={e => { if (e.key === 'Enter') applyDraft() }}
                  inputProps={{
                    style: {
                      textAlign: 'center', padding: 0, fontWeight: 700, fontSize: '1rem',
                      width: `${Math.max(2, durationDisplay.length)}ch`,
                    }
                  }}
                  sx={{ color: 'primary.main' }}
                />
                <Typography variant="body2" color="text.secondary">s</Typography>
              </Box>
              <IconButton size="small" onClick={() => props.add(1, props.item)} sx={{ width: 44, height: 44 }}>
                <Typography sx={{ fontSize: 20, lineHeight: 1, fontWeight: 300 }}>+</Typography>
              </IconButton>
            </Box>
          )}
        </Box>
      </Box>
    )
  }

  // Desktop
  return (
    <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>

      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', px: 1.25, height: 32, gap: 0.5, flexShrink: 0 }}>
        <Typography sx={{
          fontSize: '0.7rem', fontWeight: 700, color: 'text.disabled',
          letterSpacing: '0.05em', flexShrink: 0,
        }}>
          {props.item.order}
        </Typography>
        <Typography sx={{ fontSize: '0.65rem', color: 'text.disabled', flexShrink: 0 }}>·</Typography>
        <Typography sx={{
          fontSize: '0.72rem', fontWeight: 600, color: 'text.primary',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1,
        }}>
          {media.desc}
        </Typography>
        <Tooltip title={props.item.schedule ? 'Agendamento ativo' : 'Agendar'}>
          <IconButton size="small" onClick={() => props.onSchedule(props.item)} sx={{
            width: 22, height: 22, flexShrink: 0,
            color: props.item.schedule ? 'primary.main' : 'transparent',
            opacity: props.item.schedule ? 1 : 0.25,
            '&:hover': { opacity: 1, color: 'primary.main' },
            transition: 'opacity 0.15s, color 0.15s',
          }}>
            <EventOutlined sx={{ fontSize: 13 }} />
          </IconButton>
        </Tooltip>
        <IconButton size="small" onClick={() => props.removeItem(props.item)} sx={{
          width: 22, height: 22, flexShrink: 0, opacity: 0.25,
          '&:hover': { opacity: 1, color: 'error.main' },
          transition: 'opacity 0.15s, color 0.15s',
        }}>
          <Close sx={{ fontSize: 13 }} />
        </IconButton>
      </Box>

      {/* Preview */}
      <Box sx={{ flex: 1, overflow: 'hidden', position: 'relative', mx: 1, borderRadius: 1.5, bgcolor: 'grey.100', minHeight: 0 }}>
        {!imgLoaded && !imgError && (
          <Skeleton variant="rectangular" width="100%" height="100%" animation="wave"
            sx={{ position: 'absolute', inset: 0, borderRadius: 1.5 }} />
        )}
        {!imgError && (
          <Box component="img"
            src={resolveMediaUrl(media.urlPreview)}
            alt={media.desc}
            onLoad={() => setImgLoaded(true)}
            onError={() => { setImgLoaded(true); setImgError(true) }}
            sx={{ width: '100%', height: '100%', objectFit: 'cover', display: imgLoaded ? 'block' : 'none', borderRadius: 1.5 }}
          />
        )}
        {imgError && (
          <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography variant="caption" color="text.disabled" sx={{ textAlign: 'center', px: 1 }}>
              Sem preview
            </Typography>
          </Box>
        )}
        {isVideo && imgLoaded && !imgError && (
          <PlayCircleOutline sx={{
            position: 'absolute', top: '50%', left: '50%',
            transform: 'translate(-50%,-50%)',
            fontSize: 36, color: 'rgba(255,255,255,0.85)', pointerEvents: 'none',
          }} />
        )}
      </Box>

      {/* Footer */}
      <Box sx={{ px: 1.25, pt: 0.5, pb: 1, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {isLocked ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <LockOutlined sx={{ fontSize: 13, color: 'text.disabled' }} />
            <Typography sx={{ fontSize: '1.1rem', fontWeight: 700, color: 'text.secondary' }}>
              {props.item.seconds}s
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <IconButton onClick={() => props.sub(1, props.item)}
              disabled={props.item.seconds <= MIN_SEC}
              sx={{
                width: 36, height: 36, color: 'text.secondary',
                border: '1px solid', borderColor: 'divider', borderRadius: 1.5,
                '&:hover': { color: 'text.primary', bgcolor: 'action.hover' },
                '&.Mui-disabled': { opacity: 0.3 },
              }}>
              <Typography sx={{ fontSize: 22, lineHeight: 1, fontWeight: 300, userSelect: 'none' }}>−</Typography>
            </IconButton>
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.25, minWidth: 48, justifyContent: 'center' }}>
              <InputBase
                value={durationDisplay}
                onChange={e => setDraft(e.target.value.replace(/\D/g, ''))}
                onFocus={e => { setDraft(String(props.item.seconds)); e.target.select() }}
                onBlur={applyDraft}
                onKeyDown={e => { if (e.key === 'Enter') applyDraft() }}
                inputProps={{
                  style: {
                    textAlign: 'center', padding: 0,
                    fontWeight: 700, fontSize: '1.25rem',
                    width: `${Math.max(2, durationDisplay.length)}ch`,
                    color: 'inherit',
                  }
                }}
                sx={{ color: 'primary.main', cursor: 'text' }}
              />
              <Typography sx={{ fontSize: '0.9rem', color: 'text.secondary', lineHeight: 1, fontWeight: 500 }}>s</Typography>
            </Box>
            <IconButton onClick={() => props.add(1, props.item)}
              disabled={props.item.seconds >= MAX_SEC}
              sx={{
                width: 36, height: 36, color: 'text.secondary',
                border: '1px solid', borderColor: 'divider', borderRadius: 1.5,
                '&:hover': { color: 'text.primary', bgcolor: 'action.hover' },
                '&.Mui-disabled': { opacity: 0.3 },
              }}>
              <Typography sx={{ fontSize: 22, lineHeight: 1, fontWeight: 300, userSelect: 'none' }}>+</Typography>
            </IconButton>
          </Box>
        )}
      </Box>
    </Box>
  )
})

export default CardMidia
